import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Nav from "../Components/Nav";
import { getAuth, onAuthStateChanged, updatePassword } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../config";

export default function Settings() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [messages, setMessages] = useState({
    profile: "",
    password: "",
    pin: "",
    wallet: "",
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const snap = await getDoc(doc(db, "users", u.uid));
        setUserData(snap.exists() ? snap.data() : {});
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Clear message for the current tab after 3 seconds
    if (messages[activeTab]) {
      const timer = setTimeout(() => {
        setMessages((prev) => ({ ...prev, [activeTab]: "" }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [messages, activeTab]);

  const saveField = async (field, value, tab) => {
    if (!user) {
      setMessages((prev) => ({ ...prev, [tab]: "Not logged in" }));
      return;
    }
    try {
      await updateDoc(doc(db, "users", user.uid), { [field]: value });
      setUserData((prev) => ({ ...prev, [field]: value }));
      setMessages((prev) => ({ ...prev, [tab]: "Updated!" }));
    } catch (e) {
      console.error(`Error updating ${field}:`, e);
      setMessages((prev) => ({ ...prev, [tab]: "Update failed" }));
    }
  };

  const handleProfile = (e) => {
    e.preventDefault();
    const { fname, lname, address, country, bio } = e.target;
    saveField("fname", fname.value.trim(), "profile");
    saveField("lname", lname.value.trim(), "profile");
    saveField("address", address.value.trim(), "profile");
    saveField("country", country.value, "profile");
    saveField("bio", bio.value.trim(), "profile");
  };

  const handlePassword = (e) => {
    e.preventDefault();
    const { newpass } = e.target;
    if (auth.currentUser && newpass.value.length >= 6) {
      updatePassword(auth.currentUser, newpass.value)
        .then(() => {
          setMessages((prev) => ({ ...prev, password: "Password updated" }));
        })
        .catch((e) => {
          setMessages((prev) => ({ ...prev, password: e.message }));
        });
    } else {
      setMessages((prev) => ({ ...prev, password: "Min 6 chars" }));
    }
  };

  const handlePin = (e) => {
    e.preventDefault();
    const { pin } = e.target;
    saveField("pin", pin.value, "pin");
  };

  const handleWallet = (e) => {
    e.preventDefault();
    const { wallet } = e.target;
    saveField("walletAddress", wallet.value.trim(), "wallet");
  };

  return (
    <div>
      <Helmet>
        <script src="forthfunded/popper.min.js" />
        <script src="forthfunded/bootstrap.bundle.min.js" />
        <script src="forthfunded/defaultmenu.min.js" />
        <script src="forthfunded/waves.min.js" />
        <script src="forthfunded/sticky.js" />
        <script src="forthfunded/simplebar.min.js" />
        <script src="forthfunded/simplebar.js" />
        <script src="forthfunded/pickr.es5.min.js" />
        <script src="forthfunded/apexcharts.min.js" />
        <script src="forthfunded/crypto-dashboard.js" />
        <script src="forthfunded/custom-switcher.min.js" />
        <script src="forthfunded/custom.js" />
      </Helmet>


      <Header />
      <Nav />

      <div className="page main-content app-content">
        <div className="container">
          <h1 className="fs-18 fw-semibold my-4">Settings</h1>

          <ul className="nav nav-tabs mb-3">
            {["profile", "password", "pin", "wallet", "referral"].map((t) => (
              <li className="nav-item" key={t}>
                <button
                  className={`nav-link ${activeTab === t ? "active" : ""}`}
                  onClick={() => setActiveTab(t)}
                >
                  {t === "profile" && "Profile"}
                  {t === "password" && "Password"}
                  {t === "pin" && "Transaction PIN"}
                  {t === "wallet" && "Manage Wallet"}
                  {t === "referral" && "Referrals"}
                </button>
              </li>
            ))}
          </ul>

          <div className="tab-content">
            {activeTab === "profile" && userData && (
              <form onSubmit={handleProfile} className="row g-3">
                {messages.profile && (
                  <div className="alert alert-info">{messages.profile}</div>
                )}
                <div className="col-md-6">
                  <label className="form-label">First Name</label>
                  <input
                    name="fname"
                    defaultValue={userData.fname}
                    className="form-control"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Last Name</label>
                  <input
                    name="lname"
                    defaultValue={userData.lname}
                    className="form-control"
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Address</label>
                  <input
                    name="address"
                    defaultValue={userData.address}
                    className="form-control"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Country</label>
                  <select
                    name="country"
                    defaultValue={userData.country}
                    className="form-select"
                  >
                    <option value="">Select country</option>
                    <option value="Afghanistan">Afghanistan</option>
                    <option value="Albania">Albania</option>
                    <option value="Algeria">Algeria</option>
                    <option value="Andorra">Andorra</option>
                    <option value="Angola">Angola</option>
                    <option value="Antigua and Barbuda">
                      Antigua and Barbuda
                    </option>
                    <option value="Argentina">Argentina</option>
                    <option value="Armenia">Armenia</option>
                    <option value="Australia">Australia</option>
                    <option value="Austria">Austria</option>
                    <option value="Azerbaijan">Azerbaijan</option>
                    <option value="Bahamas">Bahamas</option>
                    <option value="Bahrain">Bahrain</option>
                    <option value="Bangladesh">Bangladesh</option>
                    <option value="Barbados">Barbados</option>
                    <option value="Belarus">Belarus</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Belize">Belize</option>
                    <option value="Benin">Benin</option>
                    <option value="Bhutan">Bhutan</option>
                    <option value="Bolivia">Bolivia</option>
                    <option value="Bosnia and Herzegovina">
                      Bosnia and Herzegovina
                    </option>
                    <option value="Botswana">Botswana</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Brunei">Brunei</option>
                    <option value="Bulgaria">Bulgaria</option>
                    <option value="Burkina Faso">Burkina Faso</option>
                    <option value="Burundi">Burundi</option>
                    <option value="Cabo Verde">Cabo Verde</option>
                    <option value="Cambodia">Cambodia</option>
                    <option value="Cameroon">Cameroon</option>
                    <option value="Canada">Canada</option>
                    <option value="Central African Republic">
                      Central African Republic
                    </option>
                    <option value="Chad">Chad</option>
                    <option value="Chile">Chile</option>
                    <option value="China">China</option>
                    <option value="Colombia">Colombia</option>
                    <option value="Comoros">Comoros</option>
                    <option value="Congo (Congo-Brazzaville)">
                      Congo (Congo-Brazzaville)
                    </option>
                    <option value="Costa Rica">Costa Rica</option>
                    <option value="Croatia">Croatia</option>
                    <option value="Cuba">Cuba</option>
                    <option value="Cyprus">Cyprus</option>
                    <option value="Czech Republic">Czech Republic</option>
                    <option value="Democratic Republic of the Congo">
                      Democratic Republic of the Congo
                    </option>
                    <option value="Denmark">Denmark</option>
                    <option value="Djibouti">Djibouti</option>
                    <option value="Dominica">Dominica</option>
                    <option value="Dominican Republic">
                      Dominican Republic
                    </option>
                    <option value="Ecuador">Ecuador</option>
                    <option value="Egypt">Egypt</option>
                    <option value="El Salvador">El Salvador</option>
                    <option value="Equatorial Guinea">Equatorial Guinea</option>
                    <option value="Eritrea">Eritrea</option>
                    <option value="Estonia">Estonia</option>
                    <option value="Eswatini">Eswatini</option>
                    <option value="Ethiopia">Ethiopia</option>
                    <option value="Fiji">Fiji</option>
                    <option value="Finland">Finland</option>
                    <option value="France">France</option>
                    <option value="Gabon">Gabon</option>
                    <option value="Gambia">Gambia</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Germany">Germany</option>
                    <option value="Ghana">Ghana</option>
                    <option value="Greece">Greece</option>
                    <option value="Grenada">Grenada</option>
                    <option value="Guatemala">Guatemala</option>
                    <option value="Guinea">Guinea</option>
                    <option value="Guinea-Bissau">Guinea-Bissau</option>
                    <option value="Guyana">Guyana</option>
                    <option value="Haiti">Haiti</option>
                    <option value="Honduras">Honduras</option>
                    <option value="Hungary">Hungary</option>
                    <option value="Iceland">Iceland</option>
                    <option value="India">India</option>
                    <option value="Indonesia">Indonesia</option>
                    <option value="Iran">Iran</option>
                    <option value="Iraq">Iraq</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Israel">Israel</option>
                    <option value="Italy">Italy</option>
                    <option value="Ivory Coast">Ivory Coast</option>
                    <option value="Jamaica">Jamaica</option>
                    <option value="Japan">Japan</option>
                    <option value="Jordan">Jordan</option>
                    <option value="Kazakhstan">Kazakhstan</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Kiribati">Kiribati</option>
                    <option value="Kuwait">Kuwait</option>
                    <option value="Kyrgyzstan">Kyrgyzstan</option>
                    <option value="Laos">Laos</option>
                    <option value="Latvia">Latvia</option>
                    <option value="Lebanon">Lebanon</option>
                    <option value="Lesotho">Lesotho</option>
                    <option value="Liberia">Liberia</option>
                    <option value="Libya">Libya</option>
                    <option value="Liechtenstein">Liechtenstein</option>
                    <option value="Lithuania">Lithuania</option>
                    <option value="Luxembourg">Luxembourg</option>
                    <option value="Madagascar">Madagascar</option>
                    <option value="Malawi">Malawi</option>
                    <option value="Malaysia">Malaysia</option>
                    <option value="Maldives">Maldives</option>
                    <option value="Mali">Mali</option>
                    <option value="Malta">Malta</option>
                    <option value="Marshall Islands">Marshall Islands</option>
                    <option value="Mauritania">Mauritania</option>
                    <option value="Mauritius">Mauritius</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Micronesia">Micronesia</option>
                    <option value="Moldova">Moldova</option>
                    <option value="Monaco">Monaco</option>
                    <option value="Mongolia">Mongolia</option>
                    <option value="Montenegro">Montenegro</option>
                    <option value="Morocco">Morocco</option>
                    <option value="Mozambique">Mozambique</option>
                    <option value="Myanmar (Burma)">Myanmar (Burma)</option>
                    <option value="Namibia">Namibia</option>
                    <option value="Nauru">Nauru</option>
                    <option value="Nepal">Nepal</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="Nicaragua">Nicaragua</option>
                    <option value="Niger">Niger</option>
                    <option value="Nigeria">Nigeria</option>
                    <option value="North Korea">North Korea</option>
                    <option value="North Macedonia">North Macedonia</option>
                    <option value="Norway">Norway</option>
                    <option value="Oman">Oman</option>
                    <option value="Pakistan">Pakistan</option>
                    <option value="Palau">Palau</option>
                    <option value="Palestine">Palestine</option>
                    <option value="Panama">Panama</option>
                    <option value="Papua New Guinea">Papua New Guinea</option>
                    <option value="Paraguay">Paraguay</option>
                    <option value="Peru">Peru</option>
                    <option value="Philippines">Philippines</option>
                    <option value="Poland">Poland</option>
                    <option value="Portugal">Portugal</option>
                    <option value="Qatar">Qatar</option>
                    <option value="Romania">Romania</option>
                    <option value="Russia">Russia</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Saint Kitts and Nevis">
                      Saint Kitts and Nevis
                    </option>
                    <option value="Saint Lucia">Saint Lucia</option>
                    <option value="Saint Vincent and the Grenadines">
                      Saint Vincent and the Grenadines
                    </option>
                    <option value="Samoa">Samoa</option>
                    <option value="San Marino">San Marino</option>
                    <option value="Sao Tome and Principe">
                      Sao Tome and Principe
                    </option>
                    <option value="Saudi Arabia">Saudi Arabia</option>
                    <option value="Senegal">Senegal</option>
                    <option value="Serbia">Serbia</option>
                    <option value="Seychelles">Seychelles</option>
                    <option value="Sierra Leone">Sierra Leone</option>
                    <option value="Singapore">Singapore</option>
                    <option value="Slovakia">Slovakia</option>
                    <option value="Slovenia">Slovenia</option>
                    <option value="Solomon Islands">Solomon Islands</option>
                    <option value="Somalia">Somalia</option>
                    <option value="South Africa">South Africa</option>
                    <option value="South Korea">South Korea</option>
                    <option value="South Sudan">South Sudan</option>
                    <option value="Spain">Spain</option>
                    <option value="Sri Lanka">Sri Lanka</option>
                    <option value="Sudan">Sudan</option>
                    <option value="Suriname">Suriname</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Syria">Syria</option>
                    <option value="Taiwan">Taiwan</option>
                    <option value="Tajikistan">Tajikistan</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Thailand">Thailand</option>
                    <option value="Timor-Leste">Timor-Leste</option>
                    <option value="Togo">Togo</option>
                    <option value="Tonga">Tonga</option>
                    <option value="Trinidad and Tobago">
                      Trinidad and Tobago
                    </option>
                    <option value="Tunisia">Tunisia</option>
                    <option value="Turkey">Turkey</option>
                    <option value="Turkmenistan">Turkmenistan</option>
                    <option value="Tuvalu">Tuvalu</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Ukraine">Ukraine</option>
                    <option value="United Arab Emirates">
                      United Arab Emirates
                    </option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                    <option value="Uruguay">Uruguay</option>
                    <option value="Uzbekistan">Uzbekistan</option>
                    <option value="Vanuatu">Vanuatu</option>
                    <option value="Vatican City">Vatican City</option>
                    <option value="Venezuela">Venezuela</option>
                    <option value="Vietnam">Vietnam</option>
                    <option value="Yemen">Yemen</option>
                    <option value="Zambia">Zambia</option>
                    <option value="Zimbabwe">Zimbabwe</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Bio</label>
                  <textarea
                    name="bio"
                    defaultValue={userData.bio}
                    className="form-control"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary">
                    Save Profile
                  </button>
                </div>
              </form>
            )}

            {activeTab === "password" && (
              <form onSubmit={handlePassword}>
                {messages.password && (
                  <div className="alert alert-info">{messages.password}</div>
                )}
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    name="newpass"
                    className="form-control"
                    required
                    minLength={6}
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Update Password
                </button>
              </form>
            )}

            {activeTab === "pin" && (
              <form onSubmit={handlePin}>
                {messages.pin && (
                  <div className="alert alert-info">{messages.pin}</div>
                )}
                <div className="mb-3">
                  <label className="form-label">Transaction PIN</label>
                  <input
                    type="password"
                    name="pin"
                    className="form-control"
                    defaultValue={userData?.pin || ""}
                    required
                    minLength={4}
                    maxLength={4}
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Update PIN
                </button>
              </form>
            )}

            {activeTab === "wallet" && (
              <form onSubmit={handleWallet}>
                {messages.wallet && (
                  <div className="alert alert-info">{messages.wallet}</div>
                )}
                <div className="mb-3">
                  <label className="form-label">Wallet Address</label>
                  <input
                    name="wallet"
                    className="form-control"
                    placeholder="Wallet address"
                    defaultValue={userData?.walletAddress || ""}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Save Wallet
                </button>
              </form>
            )}

            {activeTab === "referral" && (
              <div className="border p-3 rounded bg-light">
                <h5>Your referral link</h5>
                <code>
                  {window.location.origin}/signup?ref={user?.uid}
                </code>
                <p className="mt-2">
                  Referred users:{" "}
                  <strong>{userData?.referrals?.length || 0}</strong>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
